import React from "react";

import { auth } from "js/firebase/auth";
import { AdminPermission } from "common/constants";
import { buildNumber, isDevelopment, isInternalBAIUser, isStaging } from "js/config";
import getLocalStorage from "js/core/utilities/localStorage";
import db from "js/db";
import { ShowDialog, ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import RefreshTimerDialog from "js/react/components/Dialogs/RefreshTimerDialog";

const localStorage = getLocalStorage();

let isInitialized = false;

// Reload browser when new version is pushed.
export function initializeForceReloadDialog() {
    if (isInitialized) {
        return;
    }
    isInitialized = true;

    let reloadTimeoutId = null;

    db("force-reload").on("value", async function(snap) {
        const val = snap.val();

        if (val?.maintenance) {
            const user = auth().currentUser;
            if (!user) {
                // Not signed in, redirect to maintenance page
                window.location.href = "/maintenance";
                return;
            }

            if (isInternalBAIUser(user.email)) {
                // Internal BAI user, skip maintenance mode
                return;
            }

            try {
                const result = await user.getIdTokenResult();
                if (result.claims["skip-maintenance"]) {
                    // User has the skip-maintenance flag, skip maintenance mode
                    return;
                }
            } catch {
                // noop
            }

            // User is signed in but doesn't have the skip-maintenance custom claim, redirect to maintenance page
            window.location.href = "/maintenance";
            return;
        }

        const snackBarContent = (
            <p>
                <span>A new version of Beautiful.ai is available </span>
                <span className="action_item" onClick={() => window.location.reload()}>REFRESH</span>
            </p>
        );
        const mandatoryUpdateMessage = (
            "This browser tab is running an old version of the application and must\n" +
            "                be refreshed. Your work will be saved."
        );

        //do nothing if there is no value or the build numbers are the same,
        // or is local development.
        if (val == null || buildNumber >= val.buildNumber || (isDevelopment && !isStaging)) {
            clearTimeout(reloadTimeoutId);
            return;
        }

        //Check if we've already tried to force reload recently.
        //This will prevent an infinite refresh loop due to caching or any other weirdness
        //(eg: a build number that doesn't actually exist).
        const prevForceReloadTime = localStorage.getItem("lastForceReloadTime");
        const forceReloadTime = (new Date()).getTime();
        const cooldown = 1000 * 60 * 5; // 5 minutes
        if (prevForceReloadTime && forceReloadTime - parseInt(prevForceReloadTime) < cooldown) {
            reloadTimeoutId = setTimeout(() => {
                if (val.forceUpdate) {
                    ShowDialog(RefreshTimerDialog);
                } else if (val.mandatoryUpdate) {
                    ShowDialog(MandatoryUpdateDialog, { message: mandatoryUpdateMessage });
                } else if (!val.maintenance) {
                    ShowSnackBar({
                        message: snackBarContent,
                        autoHideDuration: null,
                    });
                }
            }, 1000 * 60 * 6); //try again in 6 minutes.
        } else {
            if (val.forceUpdate) {
                localStorage.setItem("lastForceReloadTime", forceReloadTime);
                ShowDialog(RefreshTimerDialog);
            } else if (val.mandatoryUpdate) {
                ShowDialog(MandatoryUpdateDialog, { message: mandatoryUpdateMessage });
            } else if (!val.maintenance) {
                ShowSnackBar({
                    message: snackBarContent,
                    autoHideDuration: null,
                });
            }
        }
    });
}
